/**
 * Buyer type for rate differentiation.
 */
export enum BuyerType {
  PUBLIC = 'PUBLIC',
  AGENT = 'AGENT',
}

/**
 * Package type - DEFAULT (base pricing) or CUSTOM (bundled package).
 */
export enum PackageType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

/**
 * Rate card extra for package-specific extras pricing.
 */
export interface RateCardExtra {
  type: string;
  name: string;
  price: {
    amount: number;
    currency: string;
  };
  gst: number;
  maxQuantityPerRoom?: number;
}

/**
 * Rate card entry for package pricing.
 */
export interface RateCardEntry {
  offering: {
    id: string;
    name?: string;
  };
  buyerType: BuyerType;
  occupancy: number;
  unitPrice: {
    amount: number;
    currency: string;
  };
  gst: number;
  /**
   * Optional extras pricing for this rate card configuration.
   */
  extras?: RateCardExtra[];
}

/**
 * Package from Nivaas
 */
export interface NivaasPackage {
  id: string;
  name: string;
  slug: string;
  description: string;
  /**
   * Type of package - DEFAULT (base pricing) or CUSTOM (bundled package).
   */
  packageType: PackageType;
  /**
   * Offerings that customers can choose from when booking this package.
   * For DEFAULT packages, this includes ALL offerings in the workspace.
   */
  allowedOfferings: Array<{
    id: string;
    name: string;
  }>;
  supplementaryItems?: Array<{
    name: string;
    price: {
      amount: number;
      currency: string;
    };
    quantity: number;
    gst: number;
  }>;
  rateCard?: RateCardEntry[];
  availableFromDate: string;
  availableUntilDate: string;
}

/**
 * Helper to check if a package is a default package.
 */
export function isDefaultPackage(pkg: NivaasPackage): boolean {
  return pkg.packageType === PackageType.DEFAULT;
}

/**
 * Helper to check if a package is a custom package.
 */
export function isCustomPackage(pkg: NivaasPackage): boolean {
  return pkg.packageType === PackageType.CUSTOM;
}
